import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  ActivityFeedResponse,
  Attachment,
  ProjectActivityResponse,
  ProjectNote,
} from '@vari-crew/schemas';
import { apiCall } from '@/lib/api';
import { downscaleImage } from '@/lib/imageDownscale';

/** Cross-task roll-up of notes / photos / videos / punch for a project. */
export function useProjectActivity(projectId: string | undefined) {
  return useQuery<ProjectActivityResponse>({
    queryKey: ['projects', projectId, 'activity'],
    enabled: !!projectId,
    queryFn: () => apiCall<ProjectActivityResponse>(`/projects/${projectId}/activity`),
  });
}

/** Shared query options so a prefetch warms the exact entry the hook reads. */
export function activityFeedOptions(limit = 20) {
  return queryOptions({
    queryKey: ['activity-feed', limit],
    queryFn: () => apiCall<ActivityFeedResponse>(`/activity?limit=${limit}`),
    placeholderData: keepPreviousData,
  });
}

/** Cross-project activity feed (supervisor/admin). Load-more bumps the limit. */
export function useActivityFeed(limit = 20) {
  return useQuery(activityFeedOptions(limit));
}

/** Office posts a note on the project itself (not tied to a task). */
export function useAddProjectNote(projectId: string) {
  const qc = useQueryClient();
  return useMutation<ProjectNote, Error, { body: string; source: 'text' | 'voice' }>({
    mutationFn: (body) =>
      apiCall<ProjectNote>(`/projects/${projectId}/notes`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'activity'] }),
  });
}

/** Upload project-level media; optionally grouped under a project note. */
export async function uploadProjectMedia(
  projectId: string,
  file: File,
  noteId?: string | null,
): Promise<Attachment> {
  const fd = new FormData();
  if (noteId) fd.append('note_id', noteId);
  fd.append('file', await downscaleImage(file));
  return apiCall<Attachment>(`/projects/${projectId}/attachments`, { method: 'POST', body: fd });
}
